import {parseRef, WeaveObjectRef} from '@wandb/weave/react';
import React, {useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {Button} from '../../../../../../Button/Button';
import {Timestamp} from '../../../../../../Timestamp';
import {Tooltip} from '../../../../../../Tooltip';
import {UserLink} from '../../../../../../UserLink';
import {formatDuration} from '../../../charts/duration';
import {useWeaveflowRouteContext} from '../../../context';
import {SmallRef} from '../../../smallRef/SmallRef';
import {getCostFromCostData, getTokensFromUsage} from '../cost/TraceCostStats';
import {
  buildEvaluationsUrlWithDatasetSelector,
  buildEvaluationsUrlWithEvaluationSelector,
  buildEvaluationsUrlWithModelFilter,
  navigateToUrlPreservingPeek,
} from './navigationUtils';
import {useEvalTabData} from './useEvalTabData';

type EvalDefinitionSourceType = 'Evaluation' | 'Model' | 'Dataset';

type EvalDefinitionRowProps = {
  label: string;
  objectRef: string;
  parsedObjectRef?: WeaveObjectRef | null;
  entity: string;
  project: string;
  objectType: EvalDefinitionSourceType;
};

const EvalDefinitionRow: React.FC<EvalDefinitionRowProps> = ({
  label,
  objectRef,
  parsedObjectRef,
  entity,
  project,
  objectType,
}) => {
  const history = useHistory();
  const location = useLocation();
  const {baseRouter} = useWeaveflowRouteContext();
  const resolvedParsedRef = useMemo(() => {
    if (parsedObjectRef) {
      return parsedObjectRef;
    }
    if (!objectRef) {
      return null;
    }
    return parseRef(objectRef) as WeaveObjectRef;
  }, [objectRef, parsedObjectRef]);

  const evaluationsUrl = useMemo(() => {
    if (!resolvedParsedRef) {
      return null;
    }

    if (objectType === 'Evaluation') {
      return buildEvaluationsUrlWithEvaluationSelector(
        baseRouter,
        entity,
        project,
        resolvedParsedRef.artifactName,
        resolvedParsedRef.artifactVersion
      );
    }
    if (objectType === 'Dataset') {
      return buildEvaluationsUrlWithDatasetSelector(
        baseRouter,
        entity,
        project,
        resolvedParsedRef.artifactName,
        resolvedParsedRef.artifactVersion
      );
    }
    if (objectType === 'Model') {
      return buildEvaluationsUrlWithModelFilter(
        baseRouter,
        entity,
        project,
        objectRef
      );
    }
    return null;
  }, [baseRouter, entity, project, resolvedParsedRef, objectRef, objectType]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!evaluationsUrl) {
      return;
    }

    e.currentTarget.blur();
    navigateToUrlPreservingPeek(history, location, evaluationsUrl);
  };

  const tooltipLabel = `Filter evaluations by this ${label.toLowerCase()}`;

  if (!resolvedParsedRef) {
    return null;
  }

  return (
    <div className="group flex items-center py-4 gap-8">
      <div className="flex min-w-0 items-center text-[14px]">
        <SmallRef objRef={resolvedParsedRef} />
      </div>
      <div className="flex items-center justify-end pr-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <Tooltip
          trigger={
            <Button
              size="small"
              variant="ghost"
              icon="filter-alt"
              onClick={handleClick}
            />
          }
          content={tooltipLabel}
          side="top"
        />
      </div>
    </div>
  );
};

export const SummarySection: React.FC = () => {
  const {call, evaluationDefinition} = useEvalTabData();
  const {evaluationRef, modelRef, datasetRef, datasetParsedRef} =
    evaluationDefinition;
  const costs = call.traceCall?.summary?.weave?.costs;
  const evaluationParsedRef = useMemo(
    () => (evaluationRef ? (parseRef(evaluationRef) as WeaveObjectRef) : null),
    [evaluationRef]
  );
  const modelParsedRef = useMemo(
    () => (modelRef ? (parseRef(modelRef) as WeaveObjectRef) : null),
    [modelRef]
  );

  const runtime = useMemo(() => {
    const latencySeconds = call.rawSpan.summary.latency_s;
    if (latencySeconds == null || latencySeconds <= 0) {
      return 'N/A';
    }
    return formatDuration(latencySeconds);
  }, [call.rawSpan.summary.latency_s]);

  // Calculate tooltip data for token and cost breakdowns by model
  const tokenTooltipData = useMemo(
    () => getTokensFromUsage(call.traceCall?.summary?.usage),
    [call.traceCall?.summary?.usage]
  );

  const costTooltipData = useMemo(() => getCostFromCostData(costs), [costs]);

  return (
    <div className="rounded-lg px-8 shadow-sm space-y-16">
      <div className="flex flex-wrap gap-x-16 gap-y-4">
        <div>
          <div className="text-xs text-moon-500">Runtime</div>
          <div className="mt-1 text-sm font-medium text-moon-800">
            {runtime}
          </div>
        </div>
        <div>
          <div className="text-xs text-moon-500">Created on</div>
          <div className="mt-1 text-sm font-medium text-moon-800">
            <Timestamp
              value={call.rawSpan.start_time_ms / 1000}
              format="relative"
            />
          </div>
        </div>
        {tokenTooltipData.tokensNum != null &&
          tokenTooltipData.tokensNum > 0 && (
            <div>
              <div className="text-xs text-moon-500">Tokens</div>
              <Tooltip
                trigger={
                  <div className="mt-1 text-sm font-medium text-moon-800">
                    {tokenTooltipData.tokens}
                  </div>
                }
                content={tokenTooltipData.tokenToolTipContent}
                side="bottom"
              />
            </div>
          )}
        {costTooltipData.costNum != null && costTooltipData.costNum > 0 && (
          <div>
            <div className="text-xs text-moon-500">Cost</div>
            <Tooltip
              trigger={
                <div className="mt-1 text-sm font-medium text-moon-800">
                  {costTooltipData.cost}
                </div>
              }
              content={costTooltipData.costToolTipContent}
              side="bottom"
            />
          </div>
        )}
        <div>
          <div className="text-xs text-moon-500">Created by</div>
          <div className="mt-1 flex items-center text-sm font-medium text-moon-800">
            <UserLink userId={call.userId} includeName placement="bottom" />
          </div>
        </div>
      </div>
      <div>
        <div className="mb-8 text-[16px] font-semibold">Definition</div>
        <div className="rounded-lg border border-moon-250 bg-moon-50 px-4">
          <div className="grid divide-y divide-moon-200">
            {evaluationRef && (
              <EvalDefinitionRow
                label="Evaluation"
                objectRef={evaluationRef}
                parsedObjectRef={evaluationParsedRef}
                entity={call.entity}
                project={call.project}
                objectType="Evaluation"
              />
            )}
            {modelRef && (
              <EvalDefinitionRow
                label="Model"
                objectRef={modelRef}
                parsedObjectRef={modelParsedRef}
                entity={call.entity}
                project={call.project}
                objectType="Model"
              />
            )}
            {datasetRef && (
              <EvalDefinitionRow
                label="Dataset"
                objectRef={datasetRef}
                parsedObjectRef={datasetParsedRef}
                entity={call.entity}
                project={call.project}
                objectType="Dataset"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
